import type {
  ChatConfirmBody,
  ChatConversation,
  ChatMessage,
  ChatSendBody,
  ChatToolTier,
} from '@klingerator/shared';
import { api, backendBaseUrl } from './api';

export interface ChatToolInfo {
  name: string;
  description: string;
  tier?: ChatToolTier;
}

/** Chat-assistent: gesprekken (axios) en de SSE-stream (fetch, want POST + JSON + Bearer). */
export const chatService = {
  async listConversations(): Promise<ChatConversation[]> {
    const { data } = await api.get<{ items: ChatConversation[] }>('/chat/conversations', { silent: true });
    return data.items;
  },
  async createConversation(): Promise<ChatConversation> {
    const { data } = await api.post<ChatConversation>('/chat/conversations', {}, { silent: true });
    return data;
  },
  async listMessages(conversationId: number): Promise<ChatMessage[]> {
    const { data } = await api.get<{ items: ChatMessage[] }>(
      `/chat/conversations/${conversationId}/messages`,
      {
        silent: true,
      },
    );
    return data.items;
  },
  async rename(conversationId: number, title: string): Promise<ChatConversation> {
    const { data } = await api.patch<ChatConversation>(
      `/chat/conversations/${conversationId}`,
      { title },
      { silent: true },
    );
    return data;
  },
  async remove(conversationId: number): Promise<void> {
    await api.delete(`/chat/conversations/${conversationId}`, { silent: true });
  },
  /** Antwoord op een bevestigingskaart; de tool-uitvoer komt daarna over de open stream. */
  async confirm(
    conversationId: number,
    toolUseId: string,
    decision: ChatConfirmBody['decision'],
  ): Promise<void> {
    await api.post(
      `/chat/conversations/${conversationId}/messages/confirm`,
      { tool_use_id: toolUseId, decision } satisfies ChatConfirmBody,
      { silent: true },
    );
  },
  async listTools(): Promise<ChatToolInfo[]> {
    const { data } = await api.get<{ items: ChatToolInfo[] }>('/chat/tools', { silent: true, noToast: true });
    return data.items;
  },

  /**
   * Opent de antwoordstroom. Fouten vóór de stream (400/404/429, JSON) worden een Error met de
   * Nederlandse melding uit de API en `status`.
   */
  async openStream(conversationId: number, body: ChatSendBody, signal: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    const auth = api.defaults.headers.common['Authorization'];
    if (typeof auth === 'string') headers.Authorization = auth;
    const res = await fetch(`${backendBaseUrl}/api/chat/conversations/${conversationId}/messages/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) {
      let message =
        res.status === 401 ? 'Sessie verlopen — log opnieuw in' : `Fout ${res.status} van de server`;
      try {
        const j = (await res.json()) as { error?: { message?: string } };
        if (j?.error?.message) message = j.error.message;
      } catch {
        /* geen JSON-body */
      }
      const err = new Error(message) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }
    if (!res.body) throw new Error('Geen antwoordstroom van de server');
    return res;
  },
};
